import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from './utils';

/**
 * Banner — inline status message for forms, dialogs, and page-level alerts.
 * Composes the soft-tinted state token (bg-state-X/10 + text-state-X) used
 * across both onboarding and backoffice for "submit failed" / "saved" / etc.
 * banners. The default `role="alert"` makes danger/warning announce to AT;
 * pass `role="status"` for non-urgent success/info banners.
 */

const bannerVariants = cva(
  'flex items-start gap-2 rounded-md p-3 text-sm',
  {
    variants: {
      tone: {
        info: 'bg-state-info/10 text-state-info',
        success: 'bg-state-success/10 text-state-success',
        warning: 'bg-state-warning/10 text-state-warning',
        danger: 'bg-state-danger/10 text-state-danger',
      },
    },
    defaultVariants: { tone: 'info' },
  },
);

const TONE_ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
} as const;

export interface BannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof bannerVariants> {
  children: React.ReactNode;
  icon?: React.ReactNode | false;
}

export function Banner({
  tone,
  className,
  role,
  icon,
  children,
  ...props
}: BannerProps): JSX.Element {
  const resolvedTone = tone ?? 'info';
  const defaultRole =
    resolvedTone === 'danger' || resolvedTone === 'warning' ? 'alert' : 'status';
  const Icon = TONE_ICONS[resolvedTone];
  return (
    <div
      role={role ?? defaultRole}
      className={cn(bannerVariants({ tone }), className)}
      {...props}
    >
      {icon === false ? null : icon !== undefined ? (
        <span aria-hidden className="mt-0.5 flex-shrink-0">
          {icon}
        </span>
      ) : (
        <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
